import { getLmsSessionToken } from "@/lib/lms"
import {
  tsRestFetchApi,
  type ClientInferRequest,
  type ClientInferResponseBody,
} from "@ts-rest/core"
import { auth } from "./auth"
import {
  contractRouter,
  baseHeaders,
  type BaseHeaders,
} from '@simulead/api/contract'
import { initTsrReactQuery } from "@ts-rest/react-query/v5"
import { useTempStore } from "./store"
import { usePermStore } from "./permStore"
import {
  apiBaseUrl,
  loginTokenLocalStorageKey,
  scenarioCreatorCallTokenLocalStorageKey,
  testingLocalStorageKey,
  workosLocalStorageKey,
} from "./utils"

function base64ToBytes(base64: any) {
  const binString = atob(base64)
  return Uint8Array.from(binString, (m) => m.codePointAt(0) as number)
}

export type ClientInput<T extends keyof typeof contractRouter> =
  ClientInferRequest<(typeof contractRouter)[T]>
export type ClientOutput<T extends keyof typeof contractRouter> =
  ClientInferResponseBody<(typeof contractRouter)[T]>

// Routes where emulation should be bypassed: the API treats them as the real
// authenticated user (privileged staff), even when an emulation is active. Used
// for admin-only data the modal needs to render while emulating, e.g. the user
// search list in EmulateDialog.
const queriesToSkipEmulationFor = [
  "getAllUsers",
  // MCP consent (ENG-1138): staff can read or cancel a Claude request while
  // emulating. approveMcpAuthorization is deliberately NOT here — it must see
  // the emulation header so the API can refuse to bind the grant.
  "getMcpAuthorizationRequest",
  "denyMcpAuthorization",
] as const satisfies ReadonlyArray<keyof typeof contractRouter>

const pathsToSkipEmulationFor: Set<string> = new Set(
  queriesToSkipEmulationFor.map((key) => contractRouter[key].path)
)

export const client = initTsrReactQuery(contractRouter, {
  baseUrl: apiBaseUrl,
  baseHeaders: {
    authorization: `Bearer`,
  },
  api: async (args) => {
    const headers = args.headers as BaseHeaders

    const workosToken = localStorage.getItem(workosLocalStorageKey)
    const testingToken = localStorage.getItem(testingLocalStorageKey)
    const loginToken = localStorage.getItem(loginTokenLocalStorageKey)

    // ENG-1476: the LMS launch window only ever uses its scoped session, so
    // a normal Ambr login in the same browser is never used or disturbed
    const lmsSessionToken = getLmsSessionToken()

    if (lmsSessionToken) {
      headers.token = lmsSessionToken
      headers["token-type"] = "lms-session"
    } else if (testingToken) {
      headers.token = testingToken
      headers["token-type"] = "testing"
    } else if (loginToken) {
      headers.token = loginToken
      headers["token-type"] = "login-token"
    } else if (workosToken) {
      headers.token = workosToken
      headers["token-type"] = "workos-profile-session"
    } else {
      const firebaseToken = await auth.currentUser?.getIdToken()
      headers.token = firebaseToken
      headers["token-type"] = "firebase"
    }

    const scenarioCreatorCallToken = localStorage.getItem(
      scenarioCreatorCallTokenLocalStorageKey
    )
    if (scenarioCreatorCallToken && !lmsSessionToken) {
      headers["scenario-creator-call-token"] = scenarioCreatorCallToken
    }

    if (headers["content-type"] === "multipart/form-data") {
      delete headers["content-type"]
    } else {
      headers["content-type"] = "application/json"
    }

    const skipEmulate = pathsToSkipEmulationFor.has(args.route.path)

    const { emulateUserId, userIdBeforeStartedEmulating } =
      usePermStore.getState()
    if (emulateUserId && !skipEmulate && !lmsSessionToken) {
      headers["emulate-user-id"] = String(emulateUserId)
    }

    const { user } = useTempStore.getState()
    if (skipEmulate) {
      // Identify the real authenticated user so the API's getSession can match
      // them. Falls back to the store user when not emulating
      // (userIdBeforeStartedEmulating is only set during emulation).
      const userIdForSkip = userIdBeforeStartedEmulating ?? user?.id
      if (userIdForSkip !== undefined) {
        headers["user-id"] = String(userIdForSkip)
      }
    } else if (user) {
      headers["user-id"] = String(user.id)
    }

    args.headers = headers
    const response = await tsRestFetchApi(args)

    return response
  },
})

export function validateClientProperty(property: keyof typeof contractRouter) {
  return property
}
